import React, { useState } from "react";

import { PassengerDetails } from "./types";

interface PassengerCabinSelectorProps {
    isOpen: boolean;
    onClose: () => void;
    initialPassengers: string;
    initialDetails?: PassengerDetails;
    onUpdate: (passengers: string, details?: PassengerDetails) => void;
}

interface PassengerType {
    label: string;
    description?: string;
    count: number;
    minCount: number;
    maxCount: number;
}

type CabinClass = "All Cabins" | "Economy" | "Premium Economy" | "Business";

const PassengerCabinSelector: React.FC<PassengerCabinSelectorProps> = ({
    isOpen,
    onClose,
    initialPassengers,
    initialDetails,
    onUpdate,
}) => {
    // Parse initial passengers string (e.g., "1 pax, All Cabins")
    const parseInitialValues = () => {
        const parts = initialPassengers.split(", ");
        const paxCount = parseInt(parts[0]) || 1;
        const cabin = (parts[1] || "All Cabins") as CabinClass;
        return { paxCount, cabin };
    };

    const { paxCount: initialPaxCount, cabin: initialCabin } =
        parseInitialValues();

    const [passengers, setPassengers] = useState<PassengerType[]>([
        {
            label: "Adult",
            description: undefined,
            count: initialDetails?.adults ?? initialPaxCount,
            minCount: 1,
            maxCount: 9,
        },
        {
            label: "Child",
            description: "2y - 11y",
            count: initialDetails?.children ?? 0,
            minCount: 0,
            maxCount: 9,
        },
        {
            label: "Infant",
            description: "16d - 23m",
            count: initialDetails?.infants ?? 0,
            minCount: 0,
            maxCount: 9,
        },
        {
            label: "Senior Citizen",
            description: "60y +",
            count: initialDetails?.seniorCitizens ?? 0,
            minCount: 0,
            maxCount: 9,
        },
        {
            label: "Person with Disability",
            description: undefined,
            count: initialDetails?.personsWithDisability ?? 0,
            minCount: 0,
            maxCount: 9,
        },
        {
            label: "Overseas Filipino Workers",
            description: undefined,
            count: initialDetails?.overseasFilipinoWorkers ?? 0,
            minCount: 0,
            maxCount: 9,
        },
    ]);

    const [selectedCabin, setSelectedCabin] =
        useState<CabinClass>(initialCabin);

    if (!isOpen) return null;

    const updatePassengerCount = (index: number, delta: number) => {
        setPassengers((prev) => {
            const newPassengers = prev.map((p, i) => {
                if (i === index) {
                    const newCount = p.count + delta;

                    if (newCount >= p.minCount && newCount <= p.maxCount) {
                        // Ensure at least one adult
                        if (index === 0 && newCount === 0) {
                            const hasOtherAdults = prev
                                .slice(3)
                                .some((passenger) => passenger.count > 0);
                            if (!hasOtherAdults) return p;
                        }

                        return { ...p, count: newCount };
                    }
                }
                return p;
            });

            return newPassengers;
        });
    };

    const getTotalPassengers = () => {
        return passengers.reduce((sum, p) => sum + p.count, 0);
    };

    const handleContinue = () => {
        const total = getTotalPassengers();
        const paxText = total === 1 ? "1 pax" : `${total} pax`;

        // Create detailed passenger breakdown
        const details: PassengerDetails = {
            adults: passengers[0].count,
            children: passengers[1].count,
            infants: passengers[2].count,
            seniorCitizens: passengers[3].count,
            personsWithDisability: passengers[4].count,
            overseasFilipinoWorkers: passengers[5].count,
        };

        onUpdate(`${paxText}, ${selectedCabin}`, details);
        onClose();
    };

    const renderInfoIcon = () => (
        <span
            className="inline-flex items-center justify-center w-4 h-4 ml-2 rounded-full text-xs font-bold"
            style={{
                color: "#0047ba",
                borderColor: "#0047ba",
                borderWidth: "1px",
                borderStyle: "solid",
            }}
        >
            i
        </span>
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex lg:items-center lg:justify-center z-50">
            <div className="bg-white lg:rounded-2xl w-full h-full lg:h-auto lg:mx-4 lg:max-w-[710px] lg:max-h-[600px] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center px-8 pt-6 pb-6">
                    <h2 className="lg:text-xl landscape:text-xl text-lg font-normal text-[#011d4f]">
                        Passenger and cabin selection
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                <div className="border-b border-gray-200"></div>

                {/* Content */}
                <div className="lg:flex landscape:flex gap-0 overflow-y-auto flex-1">
                    {/* Left side / Mobile full width - Select Passengers */}
                    <div className="lg:w-2/3 landscape:w-2/3 px-8 py-6 relative">
                        {/* Border with spacing - only on desktop/landscape */}
                        <div className="hidden lg:block landscape:block absolute right-0 top-4 bottom-4 w-px bg-gray-200"></div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-6">
                            Select Passengers
                        </h3>

                        <div className="space-y-1">
                            {passengers.map((passenger, index) => (
                                <div
                                    key={passenger.label}
                                    className="flex items-center justify-between"
                                >
                                    <div className="flex-1">
                                        <div className="flex items-center">
                                            <span className="text-gray-900 font-normal leading-tight">
                                                {passenger.label}
                                            </span>
                                            {renderInfoIcon()}
                                        </div>
                                        {passenger.description && (
                                            <span className="text-sm text-gray-500 -mt-1 block">
                                                {passenger.description}
                                            </span>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-4">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                updatePassengerCount(index, -1);
                                            }}
                                            disabled={
                                                passenger.count <=
                                                passenger.minCount
                                            }
                                            aria-label={`Decrease ${passenger.label}`}
                                            className={`w-10 h-10 rounded-full border flex items-center justify-center transition-colors ${
                                                passenger.count <=
                                                passenger.minCount
                                                    ? "border-gray-300 bg-gray-200 text-gray-400 cursor-not-allowed"
                                                    : "border-gray-400 bg-white text-gray-700 hover:bg-gray-50"
                                            }`}
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M20 12H4"
                                                />
                                            </svg>
                                        </button>

                                        <span className="w-8 text-center text-xl font-medium text-gray-900">
                                            {passenger.count}
                                        </span>

                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                updatePassengerCount(index, 1);
                                            }}
                                            disabled={
                                                passenger.count >=
                                                passenger.maxCount
                                            }
                                            aria-label={`Increase ${passenger.label}`}
                                            className={`w-10 h-10 rounded-full border flex items-center justify-center transition-colors ${
                                                passenger.count >=
                                                passenger.maxCount
                                                    ? "border-gray-300 bg-gray-200 text-gray-400 cursor-not-allowed"
                                                    : "border-gray-400 bg-white text-gray-700 hover:bg-gray-50"
                                            }`}
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 4v16m8-8H4"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right side / Mobile full width - Select cabin */}
                    <div className="lg:w-1/3 landscape:w-1/3 px-8 py-6 border-t lg:border-t-0 landscape:border-t-0 border-gray-200">
                        <h3 className="text-xl font-semibold text-gray-900 mb-6">
                            Select cabin
                        </h3>

                        <div className="space-y-5">
                            {(
                                [
                                    "All Cabins",
                                    "Economy",
                                    "Comfort",
                                    "Business",
                                ] as CabinClass[]
                            ).map((cabin) => (
                                <label
                                    key={cabin}
                                    className="flex items-center cursor-pointer"
                                >
                                    <input
                                        type="radio"
                                        name="cabin"
                                        value={cabin}
                                        checked={selectedCabin === cabin}
                                        onChange={() => setSelectedCabin(cabin)}
                                        className="sr-only"
                                    />
                                    <div className="relative">
                                        <div
                                            className={`w-6 h-6 rounded-full border-2 transition-colors ${
                                                selectedCabin === cabin
                                                    ? ""
                                                    : "border-gray-400 bg-white"
                                            }`}
                                            style={
                                                selectedCabin === cabin
                                                    ? {
                                                          backgroundColor:
                                                              "#0047ba",
                                                          borderColor:
                                                              "#0047ba",
                                                      }
                                                    : {}
                                            }
                                        >
                                            {selectedCabin === cabin && (
                                                <div className="absolute inset-0 m-2 bg-white rounded-full" />
                                            )}
                                        </div>
                                    </div>
                                    <span className="ml-3 text-gray-500 font-normal">
                                        {cabin}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="border-b border-gray-200"></div>

                {/* Footer */}
                <div className="px-8 py-6">
                    <button
                        onClick={handleContinue}
                        className="w-full lg:w-auto lg:float-right px-8 py-3 text-white rounded-lg font-semibold transition-colors"
                        style={{ backgroundColor: "#0047ba" }}
                    >
                        Continue
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PassengerCabinSelector;
